import { useState } from "react";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader } from "../ui/dialog";
import { SetRow } from "./SetRow";
import { SetCard } from "./SetCard";
import { Plus } from "lucide-react";

interface SetItem { id: string; number: number; reps: number; minutes: number; seconds: number; distance: number | ""; energy_zone: string; description?: string; }

interface SetListProps {
  sets: SetItem[];
  energyZones: string[];
  onUpdate: (id: string, field: keyof SetItem, value: any) => void;
  onDelete: (set: SetItem) => void;
  onAddSet: () => void;
  showAddButton?: boolean;
  onCopySet: (set: SetItem) => void;
  calculateTotalTime: () => string;
  calculateTotalDistance: () => number;
}

/**
 * Component for displaying the list of sets with desktop and mobile views
 */
export function SetList({
  sets,
  energyZones,
  onUpdate,
  onDelete,
  onAddSet,
  showAddButton = true,
  onCopySet,
  calculateTotalTime,
  calculateTotalDistance,
}: SetListProps) {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<SetItem | null>(null);
  const [editDraft, setEditDraft] = useState({
    reps: "1",
    distance: "0",
    minutes: "0",
    seconds: "0",
    energy_zone: energyZones[0] || "Warm Up",
    description: "",
  });

  const openEdit = (set: SetItem) => {
    setEditTarget(set);
    setEditDraft({
      reps: String(set.reps ?? 1),
      distance: String(set.distance ?? 0),
      minutes: String(set.minutes ?? 0),
      seconds: String(set.seconds ?? 0),
      energy_zone: set.energy_zone || (energyZones[0] || "Warm Up"),
      description: set.description || "",
    });
    setIsEditOpen(true);
  };

  const clampInt = (value: string, min: number, max: number) => {
    const num = parseInt(value || "0", 10);
    if (Number.isNaN(num)) return min;
    return Math.min(Math.max(num, min), max);
  };

  const handleSaveEdit = () => {
    if (!editTarget) return;
    const reps = clampInt(editDraft.reps, 1, 1000);
    const distance = clampInt(editDraft.distance, 0, 10000);
    const minutes = clampInt(editDraft.minutes, 0, 59);
    const seconds = clampInt(editDraft.seconds, 0, 59);

    onUpdate(editTarget.id, "reps", reps);
    onUpdate(editTarget.id, "distance", distance);
    onUpdate(editTarget.id, "minutes", minutes);
    onUpdate(editTarget.id, "seconds", seconds);
    onUpdate(editTarget.id, "energy_zone", editDraft.energy_zone);
    onUpdate(editTarget.id, "description", editDraft.description);

    setIsEditOpen(false);
    setEditTarget(null);
  };

  const editDialog = (
    <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
      <DialogContent className="max-h-[90vh] overflow-hidden sm:max-w-2xl">
        <DialogHeader>
          {/* <DialogTitle>Edit Set</DialogTitle> */}
        </DialogHeader>

        <div className="mt-4 max-h-[70vh] space-y-4 overflow-y-auto pr-2 pb-4">
          <div>
            <label className="block text-sm">Reps (Max: 1000)</label>
            <input
              type="number"
              className="w-full border p-2 rounded"
              value={editDraft.reps}
              min="1"
              max="1000"
              onChange={(e) =>
                setEditDraft((prev) => ({ ...prev, reps: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="block text-sm">Distance (Max: 10000m)</label>
            <input
              type="number"
              className="w-full border p-2 rounded"
              value={editDraft.distance}
              min="0"
              max="10000"
              onChange={(e) =>
                setEditDraft((prev) => ({ ...prev, distance: e.target.value }))
              }
            />
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-sm">Minutes</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={2}
                className="w-full border p-2 rounded"
                value={editDraft.minutes}
                onChange={(e) =>
                  setEditDraft((prev) => ({ ...prev, minutes: e.target.value.replace(/\D/g, "").slice(0, 2) }))
                }
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm">Seconds</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={2}
                className="w-full border p-2 rounded"
                value={editDraft.seconds}
                onChange={(e) =>
                  setEditDraft((prev) => ({ ...prev, seconds: e.target.value.replace(/\D/g, "").slice(0, 2) }))
                }
              />
            </div>
          </div>

          <div>
            <label className="block text-sm">Energy Zone</label>
            <select
              className="w-full border p-2 rounded"
              value={editDraft.energy_zone}
              onChange={(e) =>
                setEditDraft((prev) => ({ ...prev, energy_zone: e.target.value }))
              }
            >
              {energyZones.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm">Description</label>
            <input
              className="w-full border p-2 rounded"
              value={editDraft.description}
              onChange={(e) =>
                setEditDraft((prev) => ({ ...prev, description: e.target.value }))
              }
            />
          </div>
        </div>

        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => setIsEditOpen(false)}
          >
            Cancel
          </Button>
          <Button
            className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
            onClick={handleSaveEdit}
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (sets.length === 0) {
    return (
      <>
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-muted/50 rounded-lg w-full">
                <div className="col-span-2 text-sm font-medium"># of Rep</div>
                <div className="col-span-2 text-sm font-medium">Distance (m)</div>
                <div className="col-span-2 text-sm font-medium">Interval</div>
                <div className="col-span-2 text-sm font-medium">Energy Zone</div>
                <div className="col-span-2 text-sm font-medium">Description</div>
                <div className="col-span-2 text-sm font-medium text-center flex items-center justify-center gap-4">
                  Actions
                  {showAddButton && (
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={onAddSet}
                      className="h-7 w-7"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        {editDialog}
      </>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Current Sets</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {/* Desktop View */}
          <div className="hidden md:block">
            <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-muted/50 rounded-lg mb-2">
                  <div className="col-span-2 text-sm font-medium"># of Rep</div>
                  <div className="col-span-2 text-sm font-medium">Distance (m)</div>
                  <div className="col-span-2 text-sm font-medium">Interval</div>
                  <div className="col-span-2 text-sm font-medium">Energy Zone</div>
                  <div className="col-span-2 text-sm font-medium">Description</div>
                  <div className="col-span-2 text-sm font-medium text-center flex items-center justify-center gap-2">
                    Actions
                    {showAddButton && (
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        onClick={onAddSet}
                        className="h-7 w-7"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

            <div className="space-y-2">
              {sets.map((set) => (
              <SetRow
                key={set.id}
                set={set}
                energyZones={energyZones}
                onUpdate={onUpdate}
                onDelete={onDelete}
                onEdit={openEdit}
                onCopy={onCopySet}
              />
            ))}
          </div>
        </div>

          {/* Mobile View */}
          <div className="md:hidden space-y-4">
            {sets.map((set) => (
            <SetCard
              key={set.id}
              set={set}
              energyZones={energyZones}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onEdit={openEdit}
              onCopy={onCopySet}
            />
          ))}
        </div>


          {/* Totals */}
          {sets.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2 mt-2">
                <div className="flex justify-between items-center px-4 py-2 bg-primary/5 rounded-lg">
                  <span className="font-medium">Total Training Time:</span>
                  <Badge variant="default" className="px-4 py-1 text-base">
                    {calculateTotalTime()}
                  </Badge>
                </div>

                <div className="flex justify-between items-center px-4 py-2 bg-primary/5 rounded-lg">
                  <span className="font-medium">Total Distance:</span>
                  <Badge variant="secondary" className="px-4 py-1 text-base">
                    {calculateTotalDistance()} m
                  </Badge>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      {editDialog}
    </>
  );
}
